import { useEffect, useRef, useState } from 'react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/**
 * Reveal-on-scroll. Returns [ref, revealed]; pair with the .mc-reveal class
 * and data-revealed={revealed}. Fires once, then disconnects.
 *
 * Replaces the react-awesome-reveal <Reveal><Fade> nesting the old grid used,
 * which mounted two wrapper components per card.
 */
export default function useReveal({ threshold = 0.15, rootMargin = '0px 0px -8% 0px', delay = 0 } = {}) {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    // Reduced motion, or no IO support: show immediately.
    if (prefersReducedMotion() || typeof IntersectionObserver === 'undefined') {
      setRevealed(true);
      return undefined;
    }

    let timer;
    const observer = new IntersectionObserver(
      ([entry]) => {
        // A fast or programmatic scroll (anchor jump, End key, restored scroll
        // position) can carry an element past the viewport between animation
        // frames, so it never reports isIntersecting. Treat anything at or above
        // the fold as already seen, otherwise those items stay invisible forever.
        const atOrAboveFold = entry.boundingClientRect.top < window.innerHeight;
        if (!entry.isIntersecting && !atOrAboveFold) return;
        timer = window.setTimeout(() => setRevealed(true), delay);
        observer.disconnect();
      },
      { threshold, rootMargin }
    );

    observer.observe(el);
    return () => {
      observer.disconnect();
      if (timer) window.clearTimeout(timer);
    };
  }, [threshold, rootMargin, delay]);

  return [ref, revealed];
}
